import Link from 'next/link'
import classNames from 'classnames'
import { ReactSVG } from 'react-svg'
import { Feature, MediaPlayer, Work } from '@prisma/client'
import { useMediaplayerTimelineEvents, useTenantFeatures } from '@src/lib/apiClient'
import { useMemo } from 'react'
import { formatTimestamp, getStatus, PlayerStatus } from '../lib/monitoring'

interface PlayerIcon {
  src: string
  title: string
}

// Map of known player types to their icons
const iconMap: Record<string, PlayerIcon> = {
  brightsign: {
    src: '/images/BrightSign_logo.svg',
    title: 'BrightSign player.',
  },
}

const defaultPlayerIcon: PlayerIcon = {
  src: '/images/generic-mediaplayer.svg',
  title: 'Media player',
}

/**
 * Get the icon path and title for a media player based on its type
 */
const getMediaPlayerIcon = (playerType: string | null | undefined): PlayerIcon => {
  const type = playerType?.toLowerCase()

  if (type && type in iconMap) {
    return iconMap[type]
  }

  return defaultPlayerIcon
}

interface MediaPlayerListItemProps {
  mediaPlayer: MediaPlayer;
  work?: Work;
}

export const MediaPlayerListItem: React.FC<MediaPlayerListItemProps> = ({
  mediaPlayer,
  work,
}) => {
  const playerStatus = getStatus(mediaPlayer.lastTimestamp, work?.duration)
  const { data: features } = useTenantFeatures()
  const { data: timeline } = useMediaplayerTimelineEvents(mediaPlayer.id)
  const isInvalidTimeline = useMemo(() => {
    const lastTimestampInTimeline = (timeline?.reduce((a, { timestamp }) => Math.max(timestamp, a), 0)) ?? 0
    if (lastTimestampInTimeline > (mediaPlayer.duration ?? 2000) - 2000) { // 2000 is for a buffer
      return true
    }
    return false
  }, [timeline, mediaPlayer.duration])

  const playerIcon = getMediaPlayerIcon(mediaPlayer.playerType)

  return (
    <div
      key={mediaPlayer.id}
      className={classNames('mediaPlayer-list-item')}
    >
      <ReactSVG
        wrapper="span"
        src={playerIcon.src}
        title={playerIcon.title}
        className="player-icon"
      />
      <Link href={`/admin/mediaplayer/${mediaPlayer.id}`}>
        {mediaPlayer.nickname}
        -
        {mediaPlayer.id}
      </Link>
      {features?.includes(Feature.REALTIME_CONTROLS) && (
        <Link href={`/admin/mediaplayer/${mediaPlayer.id}/control`}>
          [controls]
        </Link>
      )}
      {features?.includes(Feature.MEDIAPLAYER_TIMELINES) && (
        <>
          <Link href={`/admin/mediaplayer/${mediaPlayer.id}/events`}>
            [events timeline]
          </Link>

          {isInvalidTimeline && (
            <pre className="playerStatus__critical">
              Warning! Timeline is too long.
            </pre>
          )}
        </>
      )}
      <pre className="serial-number">
        {mediaPlayer.serialNumber ? mediaPlayer.serialNumber : '-'}
      </pre>
      <pre className="ip-address">
        {mediaPlayer.ipAddress ? <a href={`http://${mediaPlayer.ipAddress}`}>{mediaPlayer.ipAddress}</a> : 'No IP yet.'}
      </pre>
      <pre
        className={classNames('last-timestamp', {
          playerStatus__ok: playerStatus === PlayerStatus.OK,
          playerStatus__warning: playerStatus === PlayerStatus.WARNING,
          playerStatus__critical: playerStatus === PlayerStatus.CRITICAL,
        })}
      >
        {formatTimestamp(mediaPlayer.lastTimestamp)}
      </pre>
    </div>
  )
}
